/**
 * Authentication Error Boundary Component
 * Handles runtime errors in authentication flows with recovery mechanisms
 */

import React, { Component, ReactNode } from 'react';
// import { AuthErrorFallback } from './AuthErrorFallback';
import { showNotification } from '@mantine/notifications';
import { IconAlertTriangle } from '@tabler/icons-react';

interface Props {
  children: ReactNode;
  fallback?: React.ComponentType<ErrorBoundaryFallbackProps>;
  onError?: (error: Error, errorInfo: React.ErrorInfo) => void;
  context?: 'login' | 'auth_context' | 'session' | 'nfc' | 'general';
  enableRetry?: boolean;
  maxRetries?: number;
  resetOnPropsChange?: boolean;
  resetKeys?: Array<string | number | boolean | null | undefined>;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: React.ErrorInfo | null;
  retryCount: number;
  errorId: string;
  timestamp: Date;
  context: Props['context'];
}

export interface ErrorBoundaryFallbackProps {
  error: Error | null;
  errorInfo: React.ErrorInfo | null;
  resetError: () => void;
  retryCount: number;
  maxRetries: number;
  canRetry: boolean;
  context: Props['context'];
  errorId: string;
  timestamp: Date;
}

export class AuthErrorBoundary extends Component<Props, State> {
  private resetTimeoutId: NodeJS.Timeout | null = null;

  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      retryCount: 0,
      errorId: '',
      timestamp: new Date(),
      context: props.context || 'general',
    };
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    // Update state to show fallback UI
    return {
      hasError: true,
      error,
      errorId: `auth_error_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`,
      timestamp: new Date(),
    };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    // Update state with error info
    this.setState({
      errorInfo,
      context: this.props.context || 'general',
    });

    // Log error for debugging
    this.logError(error, errorInfo);

    // Call custom error handler if provided
    this.props.onError?.(error, errorInfo);

    // Show notification for critical authentication errors
    this.showErrorNotification(error);

    // Report error to monitoring service if available
    this.reportError(error, errorInfo);
  }

  componentDidUpdate(prevProps: Props) {
    const { resetOnPropsChange, resetKeys } = this.props;
    const { hasError } = this.state;

    // Reset error boundary when specified props change
    if (
      hasError &&
      resetOnPropsChange &&
      resetKeys &&
      prevProps.resetKeys &&
      !this.arraysEqual(prevProps.resetKeys, resetKeys)
    ) {
      this.resetError();
    }
  }

  componentWillUnmount() {
    if (this.resetTimeoutId) {
      clearTimeout(this.resetTimeoutId);
    }
  }

  private arraysEqual = (a: any[], b: any[]): boolean => {
    return a.length === b.length && a.every((val, i) => val === b[i]);
  };

  private logError = (error: Error, errorInfo: React.ErrorInfo) => {
    const { context } = this.props;
    const { errorId, timestamp } = this.state;

    console.group(`🚨 Auth Error Boundary [${context}] - ${errorId}`);
    console.error('Timestamp:', timestamp.toISOString());
    console.error('Error:', error);
    console.error('Error Message:', error.message);
    console.error('Error Stack:', error.stack);
    console.error('Component Stack:', errorInfo.componentStack);
    console.error('Error Info:', errorInfo);
    
    // Log additional context
    console.error('Context:', {
      authContext: context,
      url: window.location.href,
      userAgent: navigator.userAgent,
      localStorage: {
        authToken: localStorage.getItem('auth_token') ? '***EXISTS***' : 'null',
        hasOtherKeys: Object.keys(localStorage).length,
      },
    });
    
    console.groupEnd();
  };

  private showErrorNotification = (error: Error) => {
    const { context } = this.props;
    
    // Determine notification message based on context
    let title = '認証エラーが発生しました';
    let message = 'システムエラーが発生しました。ページを再読み込みしてください。';

    switch (context) {
      case 'login':
        title = 'ログインエラー';
        message = 'ログイン処理中にエラーが発生しました。ページを再読み込みして再試行してください。';
        break;
      case 'auth_context':
        title = '認証システムエラー';
        message = '認証システムで問題が発生しました。ページを再読み込みしてください。';
        break;
      case 'session':
        title = 'セッションエラー';
        message = 'セッション管理でエラーが発生しました。再ログインが必要な場合があります。';
        break;
      case 'nfc':
        title = 'NFCエラー';
        message = 'NFCカード読み取り中にエラーが発生しました。カードを再度かざしてください。';
        break;
    }

    showNotification({
      title,
      message,
      color: 'red',
      icon: <IconAlertTriangle size="1rem" />,
      autoClose: false, // Keep open for user to see
    });
  };

  private reportError = (error: Error, errorInfo: React.ErrorInfo) => {
    const { context } = this.props;
    const { errorId, timestamp } = this.state;

    // In production, send error to monitoring service
    if (process.env.NODE_ENV === 'production') {
      // Example: Send to error tracking service
      const errorReport = {
        id: errorId,
        timestamp: timestamp.toISOString(),
        context,
        error: {
          name: error.name,
          message: error.message,
          stack: error.stack,
        },
        componentStack: errorInfo.componentStack,
        url: window.location.href,
        userAgent: navigator.userAgent,
        authToken: localStorage.getItem('auth_token') ? '***EXISTS***' : null,
      };

      // Send to your error tracking service
      // Example: Sentry, LogRocket, etc.
      console.log('Would send error report to monitoring service:', errorReport);
    }
  };

  private resetError = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
      retryCount: 0,
      errorId: '',
      timestamp: new Date(),
    });
  };

  private handleRetry = () => {
    const { maxRetries = 3 } = this.props;
    const { retryCount } = this.state;

    if (retryCount < maxRetries) {
      this.setState((prevState) => ({
        ...prevState,
        retryCount: prevState.retryCount + 1,
      }));

      // Reset after a brief delay to allow re-render
      this.resetTimeoutId = setTimeout(() => {
        this.resetError();
      }, 100);
    }
  };

  render() {
    const { children, fallback: CustomFallback, enableRetry = true, maxRetries = 3 } = this.props;
    const { hasError, error, errorInfo, retryCount, errorId, timestamp, context } = this.state;

    if (hasError) {
      const canRetry = enableRetry && retryCount < maxRetries;
      
      const fallbackProps: ErrorBoundaryFallbackProps = {
        error,
        errorInfo,
        resetError: this.resetError,
        retryCount,
        maxRetries,
        canRetry,
        context,
        errorId,
        timestamp,
      };

      // Use custom fallback if provided, otherwise use default
      if (CustomFallback) {
        return <CustomFallback {...fallbackProps} />;
      }

      return (
        <div>Error occurred</div>
      );
    }

    return children;
  }
}

/**
 * HOC for wrapping components with AuthErrorBoundary
 */
export function withAuthErrorBoundary<P extends object>(
  Component: React.ComponentType<P>,
  errorBoundaryProps?: Omit<Props, 'children'>
) {
  const WrappedComponent = React.forwardRef<any, P>((props, ref) => (
    <AuthErrorBoundary {...errorBoundaryProps}>
      <Component {...props} ref={ref} />
    </AuthErrorBoundary>
  ));

  WrappedComponent.displayName = `withAuthErrorBoundary(${
    Component.displayName || Component.name
  })`;

  return WrappedComponent;
}

/**
 * Hook for triggering error boundary from functional components
 */
export function useErrorHandler() {
  const [error, setError] = React.useState<Error | null>(null);

  const resetError = React.useCallback(() => {
    setError(null);
  }, []);

  const throwError = React.useCallback((error: Error | string) => {
    const errorObject = typeof error === 'string' ? new Error(error) : error;
    setError(errorObject);
  }, []);

  React.useEffect(() => {
    if (error) {
      throw error;
    }
  }, [error]);

  return { throwError, resetError };
}

export default AuthErrorBoundary;